import React from "react";
import Head from "next/head";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import TextSection from "../../components/TextSection";
import ActionSection from "../../components/ActionSection";
import NevoBanner from "../../components/NevoBanner";
import links from "../../helper/links";
import ImagesSection from "../../components/ImagesSection";
import BioSection from "../../components/BioSection";

import styles from "./styles.module.scss";
import AboutUsHeader from "../../public/assets/images/about-us-header.jpg";
import NevoStand from "../../public/assets/images/nevo-stand.jpg";
import DerekLiamAreCool from "../../public/assets/images/derek-liam-are-cool.jpg";
import DerekReilly from "../../public/assets/images/derek-reilly.jpg";
import LiamGrimes from "../../public/assets/images/liam-grimes.jpg";
import AidanQuigley from "../../public/assets/images/aidan-quigley.jpg";
import eventGTM from "../../lib/gtag";

export default function AboutUsPage() {
  return (
    <>
      <Head>
        <title>About Us | Nevo Advisory</title>
      </Head>

      <Header black transparent />

      <PageHeader
        backgroundImage={AboutUsHeader.src}
        className={styles.homeChargingHeader}
      >
        <h1 className="page-header-h1">About Us.</h1>
      </PageHeader>

      <div className={`${styles.homeChargingPage} page-holder`}>
        <TextSection
          header="Nevo is Ireland’s only dedicated electric vehicle platform."
          className={styles.aboutUsText}
          text={
            <>
              Nevo offers a comprehensive range of services catering towards
              those who want to become informed on electric vehicle ownership,
              and also to those who are ready to make the switch to electric
              motoring. Since launching in 2022, Nevo has captured the interests
              of the Irish motoring industry with its focused online solution
              enabling users to find and compare vehicles, become informed on EV
              ownership and charging solutions, and use Nevo’s journey planner
              for electric vehicles.
              <br />
              <br />
              Nevo is part of the{" "}
              <a
                className="link"
                href={links.drive_inc}
                rel="noreferrer"
                target="_blank"
                onClick={() => eventGTM('clickedOnDriveIncOnAboutUsPage')}
              >
                Drive Inc group
              </a>{" "}
              of companies which focuses on automotive technology. Along with
              Nevo, Drive Inc also has{" "}
              <a
                className="link"
                href={links.tradebid}
                rel="noreferrer"
                target="_blank"
                onClick={() => eventGTM('clickedOnTradeBidOnAboutUsPage')}
              >
                TradeBid
              </a>{" "}
              (a dealer-to-dealer marketplace),{" "}
              <a
                className="link"
                href={links.sweep}
                rel="noreferrer"
                target="_blank"
                onClick={() => eventGTM('clickedOnSweepOnAboutUsPage')}
              >
                Sweep
              </a>{" "}
              (consumer car buying platform) &{" "}
              <a
                className="link"
                href={links.instabid}
                rel="noreferrer"
                target="_blank"
                onClick={() => eventGTM('clickedOnInstaBidOnAboutUsPage')}
              >
                InstaBid
              </a>{" "}
              (a platform allowing consumers to sell their vehicle to dealers).
            </>
          }
        />

        <div className={styles.imagesHolder}>
          <ImagesSection
            image1={NevoStand.src}
            image2={DerekLiamAreCool.src}
            alt1="Nevo Stand on Expo"
            alt2="Derek and Liam are cool standing on the Nevo stand"
          />
        </div>

        <div className={`${styles.bioSectionHolder} cooler-padding`}>
          <h1 className={`${styles.bioHeader} perfect-header`}>
            Meet the Nevo Team
          </h1>

          <div className={styles.bios}>
            <BioSection
              image={DerekReilly.src}
              name="Derek Reilly"
              position="General Manager, Nevo."
              mainText="“I’m truly excited about the electrification of the national fleet over the coming years. Nevo is in the perfect position to help organisations make the transition. We’ve gathered the best team of experts together to make it as seamless as possible.”"
              secondaryText="Back in 2018, Derek drove his 1st electric vehicle. He knew then that EVs were going to be a big part of his life. In 2020, Derek launched the “EV Review Ireland” channel on YouTube. His aim is to drive the awareness and adoption of electric vehicles and this has grown to become Irelands leading EV YouTube channel. Derek reviews a variety of electric vehicles from cars, e-bikes and scooters to electric vans and buses. Derek also sits on the committee for the Irish Electric Vehicle Owners Association and the EV Association of Northern Ireland. Derek is now a regular EV contributor on national and local media and has also been the chair for the recent 2022 Irish EV Summit."
            />

            <BioSection
              image={LiamGrimes.src}
              name="Liam Grimes"
              position="Head of Operations, Nevo."
              mainText="“The transition to electric vehicles is not as simple as swapping out the old with the new. Data is core to a successful transition to electric vehicles, as without the data you’re unable to make informed decisions.”"
              secondaryText="Liam is an experienced operations leader, and now specialises in automotive and sustainability advisory. Liam is passionate about cleantech, E-mobility, electric vehicles, and sustainable transportation. With over 10 years experience in Financial Services and Management Consultancy, Liam brings a unique blend of strategic thinking, operational excellence, and industry knowledge to drive sustainable solutions in the automotive sector."
            />

            <BioSection
              image={AidanQuigley.src}
              name="Aidan Quigley"
              position="Key Account Manager, Nevo."
              mainText="“Change is inevitable in life, you can either resist it or you can choose to embrace it , adapt to it and learn how to benefit from it. By embracing change, we begin to see it as an opportunity for growth.”"
              secondaryText={
                <>
                  Aidan is a highly driven, motivated and ambitious individual with over 10 years’ experience in the automotive industry. In 2017, Aidan joined Merrion Fleet (now ALD Automotive) as an Account Manager managing a portfolio of some of ALD’s largest national accounts. A key highlight while at ALD was helping develop expert knowledge in EV’s having seen the increasing demand for expertise in this field, which ultimately led to him becoming the EV SME within the business.
                  <br/><br/>
                  In 2021, Aidan joined Tesla Inc. as Key Account Manager for Ireland where he quickly transitioned to Sales lead for B2C & B2B. During his time in Tesla, he successfully helped accelerate growth for Tesla in the market. A key area of focus for him was scaling Tesla’s B2B share in the Irish fleet segment by successfully leveraging key relationships culminating in breaking into the rental fleet sector in Ireland where the business saw healthy growth. Looking to the future, Aidan is confident that it is an extremely exciting time for the mobility space in Ireland and one full of opportunity. Aidan’s ambition is to help businesses across the country make the switch to electric and drive the electric revolution in Ireland.
                </>
              }
            />
          </div>
        </div>

        <ActionSection
          header="Arrange an initial call"
          text="We have an expert team who have years of experience who are ready to answer any of your queries about making the switch to electric."
          buttonText="Contact Us"
          href="/contact-us"
        />

        <NevoBanner />
      </div>

      <Footer />
    </>
  );
}
